import React, { useEffect, useState } from "react";
import { Download, ExternalLink, Share2 } from "lucide-react";

const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const ua = () => navigator.userAgent || "";
const isAndroid = () => /Android/i.test(ua());
// WhatsApp / Instagram / Facebook / Telegram in-app browsers (or a WebView) cannot install the PWA — must open in Chrome first.
const inAppBrowser = () => /\bwv\b|WhatsApp|Instagram|FBAN|FBAV|Telegram|Line\//i.test(ua());
const chromeIntent = () => `intent://${window.location.host}${window.location.pathname}${window.location.search}#Intent;scheme=https;package=com.android.chrome;end`;

/* Recharge page nudge: install the app so "Share → Royal Lakshmi" shows up in GPay's share sheet. Android only, hidden once installed. */
export const InstallNudge = ({ tx }) => {
  const [prompt, setPrompt] = useState(window.__deferredInstallPrompt || null);
  const [installed, setInstalled] = useState(isStandalone());
  const [showHow, setShowHow] = useState(false);
  useEffect(() => {
    const onAvail = () => setPrompt(window.__deferredInstallPrompt);
    const onInstalled = () => setInstalled(true);
    window.addEventListener("pwa-installable", onAvail);
    window.addEventListener("pwa-installed", onInstalled);
    return () => { window.removeEventListener("pwa-installable", onAvail); window.removeEventListener("pwa-installed", onInstalled); };
  }, []);
  if (installed || !isAndroid()) return null;
  const install = async () => {
    if (!prompt) return setShowHow(true);
    prompt.prompt();
    const res = await prompt.userChoice;
    if (res?.outcome === "accepted") setInstalled(true);
    window.__deferredInstallPrompt = null; setPrompt(null);
  };
  const inApp = inAppBrowser();
  return (
    <div className="rounded-2xl border-2 border-blue-200 bg-blue-50 p-3" data-testid="install-nudge" data-mode={inApp ? "inapp" : prompt ? "prompt" : "manual"}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0"><Share2 className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-black text-sm text-slate-900">{tx("Install the app → pay in 1 step")}</div>
          <div className="text-[11px] text-slate-600">{tx("After paying in GPay, tap Share → Royal Lakshmi. Receipt reaches us instantly — no screenshot.")}</div>
        </div>
        {inApp ? (
          <a href={chromeIntent()} className="rl-gold-bg text-xs font-black px-3 py-2 rounded-xl shrink-0 flex items-center gap-1" data-testid="install-nudge-open-chrome"><ExternalLink className="w-3.5 h-3.5" />{tx("Open in Chrome")}</a>
        ) : (
          <button onClick={install} className="rl-gold-bg text-xs font-black px-3 py-2 rounded-xl shrink-0 flex items-center gap-1" data-testid="install-nudge-btn"><Download className="w-3.5 h-3.5" />{tx("Install app")}</button>
        )}
      </div>
      {inApp && <div className="mt-2 text-[10px] text-slate-600" data-testid="install-nudge-inapp-hint">{tx("You are inside WhatsApp's browser — open royallakshmi.com in Chrome, then tap Install.")}</div>}
      {showHow && !inApp && (
        <ol className="mt-2 text-[11px] text-slate-700 space-y-0.5 pl-1" data-testid="install-nudge-how">
          <li>1️⃣ {tx("Chrome menu (⋮, top right)")}</li>
          <li>2️⃣ {tx("Tap \"Add to Home screen\" / \"Install app\"")}</li>
          <li>3️⃣ {tx("Open Royal Lakshmi from the home screen once — done")}</li>
        </ol>
      )}
    </div>
  );
};
